import React, { useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Button, Alert } from 'react-bootstrap';
import { BranchOfficeFetchAPI } from '../../api/BranchOffices';
import { MachinesFetchAPI } from '../../api/Machines';
import ErrorAlert from '../Alerts/ErrorAlert';

// Pestaña "Mesa" del modal de configuracion del POS. Edita las mismas columnas de la sucursal que el
// formulario de sucursales del dashboard (EJS): mesas, meseros, localizador, pre-pago de mesas y la
// maquina que imprime la comanda de las mesas pre-pago (ver TableKitchenPrinter). Guarda por
// PUT /branchoffice/:id/table-settings, que solo toca esas columnas.
const FLAG_FIELDS = [
    { key: 'tables_enabled', label: '¿Usara mesas en esta sucursal?' },
    { key: 'waiters_enabled', label: '¿Usara meseros en esta sucursal?' },
    { key: 'locator', label: '¿Localizador en la mesa en esta sucursal?' },
    { key: 'prepay_tables_enabled', label: '¿Se prepagaran las mesas?' },
];

const readBranchoffice = () => {
    try {
        return JSON.parse(localStorage.getItem('branchoffice')) || {};
    } catch (err) {
        return {};
    }
};

const TableSettingsTab = () => {
    const token = localStorage.getItem('x-access-machine-token');
    const [branchoffice] = useState(readBranchoffice);
    const [flags, setFlags] = useState(() => FLAG_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: Number(branchoffice[key]) === 1 }), {}));
    const [printMachineCode, setPrintMachineCode] = useState(branchoffice.table_kitchen_print_machine_code || '');
    const [machines, setMachines] = useState([]);
    const [saving, setSaving] = useState(false);

    // Maquinas de la sucursal (cajas y POS) para elegir cual imprime la comanda.
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const lists = await Promise.all([1, 3].map(async (type) => {
                try {
                    const response = await MachinesFetchAPI.getMachinesByBranchOfficeAndType(branchoffice.id, type, token);
                    return response?.data?.data || [];
                } catch (err) {
                    return [];
                }
            }));
            if (!cancelled) setMachines(lists.flat());
        };
        if (branchoffice.id) load();
        return () => { cancelled = true; };
    }, [branchoffice.id, token]);

    // Si el codigo guardado ya no esta en la lista (maquina borrada) se sigue mostrando para poder limpiarlo.
    const machineOptions = useMemo(() => {
        const options = machines.map((machine) => ({ code: machine.code, label: `${machine.machine_name} (${machine.code})` }));
        if (printMachineCode && !options.some((option) => option.code === printMachineCode)) {
            options.push({ code: printMachineCode, label: `Maquina ${printMachineCode}` });
        }
        return options;
    }, [machines, printMachineCode]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...FLAG_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: flags[key] ? 1 : 0 }), {}),
                table_kitchen_print_machine_code: printMachineCode || '',
            };
            const response = await BranchOfficeFetchAPI.updateTableSettings(branchoffice.id, payload, token);
            const saved = response?.data?.branchoffice || payload;

            // El resto de la app lee la sucursal de localStorage: se actualiza aqui sin volver a iniciar sesion.
            const next = { ...readBranchoffice(), ...saved };
            localStorage.setItem('branchoffice', JSON.stringify(next));
            window.dispatchEvent(new CustomEvent('smartpay:branchoffice-updated', { detail: next }));

            ErrorAlert('', 'Configuración de mesas guardada', 'success');
        } catch (err) {
            ErrorAlert(err?.response?.data?.error || 'No se pudo guardar la configuración de mesas', 'Configuración de mesas', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className='container'>
            <div className='row d-flex justify-content-center'>
                <div className='col-md-8'>
                    <h3 className='text-center mb-4'><i className='uil uil-restaurant'></i> Mesa</h3>

                    <Form>
                        {FLAG_FIELDS.map(({ key, label }) => (
                            <Form.Check
                                key={key}
                                type='switch'
                                id={`table-setting-${key}`}
                                className='mb-3'
                                label={label}
                                checked={flags[key]}
                                onChange={(e) => setFlags((prev) => ({ ...prev, [key]: e.target.checked }))}
                            />
                        ))}

                        <Form.Group className='mb-3 mt-4'>
                            <Form.Label>Maquina que imprime la comanda de las mesas pre-pago</Form.Label>
                            <Form.Select value={printMachineCode} onChange={(e) => setPrintMachineCode(e.target.value)}>
                                <option value=''>Ninguna (no imprimir automaticamente)</option>
                                {machineOptions.map((option) => (
                                    <option key={option.code} value={option.code}>{option.label}</option>
                                ))}
                            </Form.Select>
                            <Form.Text muted>
                                En pre-pago el pedido llega a cocina sin que el mesero lo envie. Solo esta maquina, con el POS abierto,
                                imprime la comanda; asi no sale una por cada caja de la sucursal.
                            </Form.Text>
                        </Form.Group>

                        {!printMachineCode ? (
                            <Alert variant='warning' className='py-2'>
                                Sin una maquina asignada, los pedidos pre-pago no se imprimen en cocina automaticamente.
                            </Alert>
                        ) : null}

                        <div className='d-flex justify-content-end'>
                            <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default TableSettingsTab;
